import * as THREE from 'three/webgpu';
import {
	Fn,
	float,
	instanceIndex,
	mx_noise_vec3,
	mx_worley_noise_vec3,
	textureStore,
	uniform,
	vec3,
	vec4
} from 'three/tsl';

function makeNoiseComputeNode( texture, size, frequency, seedUniform, name, useWorley = false ) {

	const computeNoise = Fn( () => {

		const id = instanceIndex;
		const x = id.mod( size );
		const y = id.div( size ).mod( size );
		const z = id.div( size * size );
		const coordinate = vec3( x, y, z ).add( 0.5 ).div( size );
		const seedOffset = vec3(
			seedUniform.mul( 0.113 ).add( 17.1 ),
			seedUniform.mul( 0.071 ).add( 31.7 ),
			seedUniform.mul( 0.097 ).add( 47.3 )
		);
		const p = coordinate.mul( frequency ).add( seedOffset );

		const low = mx_noise_vec3( p ).mul( 0.5 ).add( 0.5 ).clamp();
		const middle = mx_noise_vec3( p.mul( 2.031 ).add( vec3( 11.7, 3.1, 7.9 ) ) ).mul( 0.5 ).add( 0.5 ).clamp();
		const high = mx_noise_vec3( p.mul( 4.113 ).add( vec3( 5.3, 19.1, 2.7 ) ) ).mul( 0.5 ).add( 0.5 ).clamp();
		const billow = float( 1 ).sub( low.x.mul( 2 ).sub( 1 ).abs() );
		const ridge = float( 1 ).sub( middle.z.mul( 2 ).sub( 1 ).abs() );
		const cellular = useWorley
			? float( 1 ).sub( mx_worley_noise_vec3( p.mul( 0.86 ), 0.92 ).x ).clamp()
			: billow;

		const data = vec4(
			low.x.mul( 0.34 ).add( billow.mul( 0.26 ) ).add( cellular.mul( 0.4 ) ),
			middle.y,
			high.z,
			billow.mul( 0.65 ).add( ridge.mul( 0.35 ) )
		).clamp();

		textureStore( texture, vec3( x, y, z ), data );

	} );

	return computeNoise().compute( size * size * size ).setName( name );

}

export class CloudNoiseResources {

	constructor( renderer, options = {} ) {

		this.renderer = renderer;
		this.baseResolution = options.baseResolution ?? 96;
		this.detailResolution = options.detailResolution ?? 48;
		this.seed = options.seed ?? 11;
		this.seedUniform = uniform( this.seed );

		this.baseTexture = this.#makeTexture( this.baseResolution, 'CyborgClouds base field' );
		this.detailTexture = this.#makeTexture( this.detailResolution, 'CyborgClouds detail field' );

		this.baseComputeNode = makeNoiseComputeNode(
			this.baseTexture,
			this.baseResolution,
			3.35,
			this.seedUniform,
			'generateCloudBaseField',
			true
		);
		this.detailComputeNode = makeNoiseComputeNode(
			this.detailTexture,
			this.detailResolution,
			5.75,
			this.seedUniform,
			'generateCloudDetailField'
		);

		this.lastComputeMs = 0;

	}

	#makeTexture( size, name ) {

		const texture = new THREE.Storage3DTexture( size, size, size );
		texture.name = name;
		texture.generateMipmaps = false;
		texture.minFilter = THREE.LinearFilter;
		texture.magFilter = THREE.LinearFilter;
		texture.wrapS = THREE.MirroredRepeatWrapping;
		texture.wrapT = THREE.MirroredRepeatWrapping;
		texture.wrapR = THREE.MirroredRepeatWrapping;
		texture.colorSpace = THREE.NoColorSpace;
		return texture;

	}

	async generate( seed = this.seed ) {

		this.seed = seed;
		this.seedUniform.value = seed;
		const start = performance.now();

		await this.renderer.computeAsync( [ this.baseComputeNode, this.detailComputeNode ] );

		this.lastComputeMs = performance.now() - start;
		return this;

	}

	get approximateBytes() {

		return ( this.baseResolution ** 3 + this.detailResolution ** 3 ) * 4;

	}

	dispose() {

		this.baseComputeNode.dispose();
		this.detailComputeNode.dispose();
		this.baseTexture.dispose();
		this.detailTexture.dispose();

	}

}
